import React, { useState } from 'react';
import { Link, useLocation } from 'wouter';
import { useUser } from '@clerk/clerk-react';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { ArrowLeft, FileText, Book, Download, Copy, Check } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from '@/hooks/use-toast';
import { Switch } from "@/components/ui/switch";

const LectureGenerator = () => {
  const [, navigate] = useLocation();
  const { user } = useUser();
  const { toast } = useToast();
  
  const [lectureTitle, setLectureTitle] = useState('');
  const [lectureDescription, setLectureDescription] = useState('');
  const [slideContent, setSlideContent] = useState('');
  const [generatedCode, setGeneratedCode] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [generateHTML, setGenerateHTML] = useState(false);

  // Check if user is authorized (anant@nextgenerationmedicine.co)
  const isAuthorized = user?.emailAddresses?.[0]?.emailAddress === 'anant@nextgenerationmedicine.co';

  const generateLecture = () => {
    if (!lectureTitle || !slideContent) {
      toast({
        title: "Missing Information",
        description: "Please provide both lecture title and slide content.",
        variant: "destructive"
      });
      return;
    }

    setIsGenerating(true);
    
    // Generate the lecture component code
    setTimeout(() => {
      const componentName = lectureTitle
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
      
      const fileName = lectureTitle
        .toLowerCase()
        .replace(/[^a-zA-Z0-9\s]/g, '')
        .replace(/\s+/g, '-');

      const slides = parseSlideContent(slideContent);
      
      const code = generateHTML 
        ? generateStaticHTML(lectureTitle, lectureDescription, slides, fileName)
        : generateLectureComponent(componentName, lectureTitle, lectureDescription, slides);
        
      setGeneratedCode(code);
      setIsGenerating(false);
      
      toast({
        title: generateHTML ? "HTML Generated" : "Lecture Generated",
        description: generateHTML 
          ? `Static HTML for ${lectureTitle} has been generated successfully.`
          : `${componentName} component has been generated successfully.`
      });
    }, 1000);
  };

  const parseSlideContent = (content: string) => {
    const slides: Array<{id: number, title: string, content: string}> = [];
    const slideBlocks = content.split(/^#{1,2}\s+/m).filter(block => block.trim());
    
    slideBlocks.forEach((block, index) => {
      const lines = block.trim().split('\n');
      const title = lines[0];
      const content = lines.slice(1).join('\n').trim();
      
      if (title && content) {
        slides.push({
          id: index + 1,
          title: title.replace(/^\d+\.\s*/, ''),
          content: content
        });
      }
    });
    
    return slides;
  };

  const generateLectureComponent = (componentName: string, title: string, description: string, slides: Array<{id: number, title: string, content: string}>) => {
    return `import React from 'react';
import { Link, useLocation } from 'wouter';
import { 
  Lightbulb, 
  AlertTriangle, 
  Info, 
  ArrowLeft, 
  FileText,
  Book,
  CheckCircle,
  PlusCircle,
  MinusCircle
} from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import Slideshow, { SlideContent } from '@/components/lectures/Slideshow';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';

// KeyTakeaway component
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Clinical Note component
const ClinicalNote = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <AlertTriangle className="text-amber-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-amber-800 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Evidence Box component
const EvidenceBox = ({ title, children }: { title: string, children: React.ReactNode }) => {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md p-5 my-6">
      <div className="flex items-center mb-2">
        <Info className="text-gray-600 h-5 w-5 mr-2" />
        <h4 className="font-semibold text-gray-900">{title}</h4>
      </div>
      <div className="text-gray-700 text-sm">
        {children}
      </div>
    </div>
  );
};

const lectureSlides: SlideContent[] = [
${slides.map((slide, index) => `  {
    title: "${slide.title}",
    content: (
      <>
        ${generateSlideContent(slide.content)}
      </>
    ),
  }${index < slides.length - 1 ? ',' : ''}`).join('\n')}
];

const ${componentName} = () => {
  const [, navigate] = useLocation();
  
  return (
    <MentorshipAccessGate>
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
        <div className="flex items-center justify-between mb-6">
          <Button 
            variant="ghost" 
            className="flex items-center text-blue-600"
            onClick={() => navigate("/mentorship")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Mentorship
          </Button>
        </div>
        
        <div>
          {/* Breadcrumb Navigation */}
          <Breadcrumb className="mb-6">
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link href="/mentorship">Mentorship</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <span className="text-gray-600">${title}</span>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
            ${title}
          </h1>
          <p className="text-[#7F8C8D] mb-6">
            ${description || 'A comprehensive exploration of key concepts and clinical applications.'}
          </p>
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center text-sm text-[#7F8C8D]">
              <FileText className="h-4 w-4 mr-1" />
              <span>Lecture Duration: 45 minutes</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-gray-300"></div>
            <div className="flex items-center text-sm text-[#7F8C8D]">
              <Book className="h-4 w-4 mr-1" />
              <span>Last Updated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
            </div>
          </div>
          
          <Separator />
          
          <Slideshow slides={lectureSlides} />
        </div>
      </div>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
};

export default ${componentName};`;
  };

  const generateSlideContent = (content: string) => {
    // Parse content and generate appropriate JSX
    const lines = content.split('\n').filter(line => line.trim());
    let jsx = '';
    
    lines.forEach(line => {
      line = line.trim();
      
      if (line.startsWith('**KeyTakeaway:**')) {
        jsx += `        <KeyTakeaway>\n          ${line.replace('**KeyTakeaway:**', '').trim()}\n        </KeyTakeaway>\n`;
      } else if (line.startsWith('**ClinicalNote:**')) {
        jsx += `        <ClinicalNote>\n          ${line.replace('**ClinicalNote:**', '').trim()}\n        </ClinicalNote>\n`;
      } else if (line.startsWith('**Evidence:**')) {
        jsx += `        <EvidenceBox title="Clinical Evidence">\n          ${line.replace('**Evidence:**', '').trim()}\n        </EvidenceBox>\n`;
      } else if (line.startsWith('- ')) {
        if (!jsx.includes('<ul className="list-disc list-inside space-y-2 mb-4">')) {
          jsx += '        <ul className="list-disc list-inside space-y-2 mb-4">\n';
        }
        jsx += `          <li>${line.substring(2)}</li>\n`;
      } else if (line.includes('**') || line.includes('*')) {
        // Handle bold and italic text
        const formatted = line
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>');
        jsx += `        <p className="mb-4">\n          ${formatted}\n        </p>\n`;
      } else if (line) {
        jsx += `        <p className="mb-4">\n          ${line}\n        </p>\n`;
      }
    });
    
    // Close any open ul tags
    if (jsx.includes('<ul className="list-disc list-inside space-y-2 mb-4">') && !jsx.includes('</ul>')) {
      jsx += '        </ul>\n';
    }
    
    return jsx;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode);
      setCopied(true);
      toast({
        title: "Copied!",
        description: "Component code copied to clipboard."
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast({
        title: "Copy Failed",
        description: "Unable to copy to clipboard.",
        variant: "destructive"
      });
    }
  };

  const generateUniqueId = () => {
    return `lecture-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const parseContentToHTML = (content: string) => {
    const lines = content.split('\n').filter(line => line.trim());
    let html = '';
    let inList = false;
    
    lines.forEach(line => {
      line = line.trim();
      
      if (line.startsWith('**KeyTakeaway:**')) {
        if (inList) {
          html += '</ul>\n';
          inList = false;
        }
        html += `<div class="key-takeaway">\n  <strong>Key Takeaway:</strong> ${line.replace('**KeyTakeaway:**', '').trim()}\n</div>\n`;
      } else if (line.startsWith('**ClinicalNote:**')) {
        if (inList) {
          html += '</ul>\n';
          inList = false;
        }
        html += `<div class="clinical-note">\n  <strong>Clinical Note:</strong> ${line.replace('**ClinicalNote:**', '').trim()}\n</div>\n`;
      } else if (line.startsWith('**Evidence:**')) {
        if (inList) {
          html += '</ul>\n';
          inList = false;
        }
        html += `<div class="evidence-box">\n  <strong>Evidence:</strong> ${line.replace('**Evidence:**', '').trim()}\n</div>\n`;
      } else if (line.startsWith('- ')) {
        if (!inList) {
          html += '<ul>\n';
          inList = true;
        }
        html += `  <li>${line.substring(2)}</li>\n`;
      } else if (line) {
        if (inList) {
          html += '</ul>\n';
          inList = false;
        }
        // Handle bold and italic text
        const formatted = line
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
          .replace(/\*(.*?)\*/g, '<em>$1</em>');
        html += `<p>${formatted}</p>\n`;
      }
    });
    
    // Close any open ul tags
    if (inList) {
      html += '</ul>\n';
    }
    
    return html;
  };

  const generateStaticHTML = (title: string, description: string, slides: Array<{id: number, title: string, content: string}>, fileName: string) => {
    const lectureId = generateUniqueId();
    const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
      line-height: 1.6;
      color: #333;
      background: #f5f5f5;
      padding: 20px;
    }
    
    .container {
      max-width: 900px;
      margin: 0 auto;
    }
    
    h1 {
      color: #2C3E50;
      font-size: 2.5rem;
      margin-bottom: 0.5rem;
      font-weight: 700;
    }
    
    .description {
      color: #7F8C8D;
      font-size: 1.1rem;
      margin-bottom: 2rem;
    }
    
    .slide {
      background: white;
      border-radius: 8px;
      padding: 2rem;
      margin-bottom: 1.5rem;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      transition: box-shadow 0.3s ease;
    }
    
    .slide:hover {
      box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    }
    
    .slide h2 {
      color: #2C3E50;
      margin-bottom: 1.5rem;
      font-size: 1.75rem;
      font-weight: 600;
    }
    
    .slide p {
      margin-bottom: 1rem;
      line-height: 1.8;
    }
    
    .slide ul {
      margin: 1rem 0;
      padding-left: 1.5rem;
    }
    
    .slide li {
      margin-bottom: 0.5rem;
    }
    
    .key-takeaway {
      background: #EBF8FF;
      border-left: 4px solid #3182CE;
      padding: 1rem;
      margin: 1.5rem 0;
      border-radius: 0 4px 4px 0;
    }
    
    .key-takeaway strong {
      color: #2B6CB0;
    }
    
    .clinical-note {
      background: #FFFBEB;
      border-left: 4px solid #F59E0B;
      padding: 1rem;
      margin: 1.5rem 0;
      border-radius: 0 4px 4px 0;
    }
    
    .clinical-note strong {
      color: #D97706;
    }
    
    .evidence-box {
      background: #F9FAFB;
      border: 1px solid #E5E7EB;
      padding: 1rem;
      margin: 1.5rem 0;
      border-radius: 4px;
    }
    
    .evidence-box strong {
      color: #4B5563;
    }
    
    .metadata {
      position: fixed;
      top: 10px;
      right: 10px;
      background: rgba(255,255,255,0.95);
      padding: 0.75rem;
      border-radius: 4px;
      font-size: 0.75rem;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      font-family: monospace;
    }
    
    .metadata div {
      margin-bottom: 0.25rem;
    }
    
    .slide-navigation {
      position: fixed;
      bottom: 20px;
      right: 20px;
      display: flex;
      gap: 10px;
    }
    
    .nav-button {
      background: #3182CE;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
    }
    
    .nav-button:hover {
      background: #2B6CB0;
    }
    
    .nav-button:disabled {
      background: #CBD5E0;
      cursor: not-allowed;
    }
    
    @media (max-width: 768px) {
      body {
        padding: 10px;
      }
      
      h1 {
        font-size: 2rem;
      }
      
      .slide {
        padding: 1.5rem;
      }
      
      .metadata {
        display: none;
      }
    }
  </style>
</head>
<body>
  <div class="metadata">
    <div><strong>ID:</strong> ${lectureId}</div>
    <div><strong>Generated:</strong> ${new Date().toISOString()}</div>
    <div><strong>Version:</strong> 1.0.0</div>
    <div><strong>File:</strong> ${fileName}.html</div>
  </div>
  
  <div class="container">
    <h1>${title}</h1>
    <p class="description">${description || 'A comprehensive exploration of key concepts and clinical applications.'}</p>
    
    ${slides.map((slide, index) => `
    <div class="slide" data-slide-id="${slide.id}" data-slide-index="${index}">
      <h2>Slide ${index + 1}: ${slide.title}</h2>
      ${parseContentToHTML(slide.content)}
    </div>
    `).join('')}
  </div>
  
  <div class="slide-navigation">
    <button class="nav-button" onclick="scrollToTop()">Back to Top</button>
  </div>
  
  <script>
    // Send height to parent for iframe resizing
    function sendHeight() {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ 
        type: 'resize', 
        height: height,
        lectureId: '${lectureId}'
      }, '*');
    }
    
    // Send height on load and resize
    window.addEventListener('load', sendHeight);
    window.addEventListener('resize', sendHeight);
    
    // Track which slides are viewed
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          window.parent.postMessage({
            type: 'slideViewed',
            slideId: entry.target.dataset.slideId,
            slideIndex: entry.target.dataset.slideIndex,
            lectureId: '${lectureId}'
          }, '*');
        }
      });
    }, { threshold: 0.5 });
    
    document.querySelectorAll('.slide').forEach(slide => {
      observer.observe(slide);
    });
    
    // Scroll to top function
    function scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    // Send ready message
    window.parent.postMessage({ 
      type: 'ready',
      lectureId: '${lectureId}'
    }, '*');
  </script>
</body>
</html>`;
    
    return htmlContent;
  };

  if (!isAuthorized) {
    return (
      <MentorshipAccessGate>
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Restricted</h1>
              <p className="text-gray-600">This tool is only available to anant@nextgenerationmedicine.co</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate("/mentorship")}
              >
                Back to Mentorship
              </Button>
            </div>
          </div>
          <Footer />
        </div>
      </MentorshipAccessGate>
    );
  }

  return (
    <MentorshipAccessGate>
      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
          <div className="flex items-center justify-between mb-6">
            <Button 
              variant="ghost" 
              className="flex items-center text-blue-600"
              onClick={() => navigate("/mentorship")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Mentorship
            </Button>
          </div>
          
          <div>
            {/* Breadcrumb Navigation */}
            <Breadcrumb className="mb-6">
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link href="/mentorship">Mentorship</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <span className="text-gray-600">Lecture Generator</span>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
              Lecture Generator
            </h1>
            <p className="text-[#7F8C8D] mb-6">
              Generate consistent lecture components using the inflammation lecture template. 
              Input your content and get a fully formatted lecture component.
            </p>
            
            <Separator />
            
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Input Section */}
              <div className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Lecture Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label htmlFor="title">Lecture Title</Label>
                      <Input
                        id="title"
                        value={lectureTitle}
                        onChange={(e) => setLectureTitle(e.target.value)}
                        placeholder="e.g., Advanced Peptide Protocols"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="description">Lecture Description (Optional)</Label>
                      <Input
                        id="description"
                        value={lectureDescription}
                        onChange={(e) => setLectureDescription(e.target.value)}
                        placeholder="Brief description of the lecture content"
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="content">Slide Content</Label>
                      <Textarea
                        id="content"
                        value={slideContent}
                        onChange={(e) => setSlideContent(e.target.value)}
                        placeholder={`Format your slides like this:

## Slide 1: Introduction
Introduction content here. Use **bold** for emphasis.

**KeyTakeaway:** Important points go here.

## Slide 2: Core Concepts
- Bullet point 1
- Bullet point 2

**ClinicalNote:** Clinical insights here.

**Evidence:** Research findings here.`}
                        className="min-h-[300px]"
                      />
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Switch
                        id="generateHTML"
                        checked={generateHTML}
                        onCheckedChange={setGenerateHTML}
                      />
                      <Label htmlFor="generateHTML" className="cursor-pointer">
                        Generate Static HTML instead of React Component
                      </Label>
                    </div>
                    
                    <Button 
                      onClick={generateLecture} 
                      disabled={isGenerating}
                      className="w-full"
                    >
                      {isGenerating ? 'Generating...' : `Generate ${generateHTML ? 'HTML' : 'React Component'}`}
                    </Button>
                  </CardContent>
                </Card>
              </div>
              
              {/* Output Section */}
              <div className="space-y-6">
                {generatedCode && (
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle>Generated Component</CardTitle>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyToClipboard}
                        className="flex items-center gap-2"
                      >
                        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        {copied ? 'Copied!' : 'Copy'}
                      </Button>
                    </CardHeader>
                    <CardContent>
                      <pre className="bg-gray-50 p-4 rounded-lg overflow-auto max-h-[600px] text-xs">
                        <code>{generatedCode}</code>
                      </pre>
                    </CardContent>
                  </Card>
                )}
                
                <Card>
                  <CardHeader>
                    <CardTitle>Instructions</CardTitle>
                  </CardHeader>
                  <CardContent className="text-sm text-gray-700 space-y-2">
                    <p><strong>1.</strong> Fill in the lecture title and content using the format shown.</p>
                    <p><strong>2.</strong> Use ## for slide titles, **KeyTakeaway:**, **ClinicalNote:**, and **Evidence:** for special sections.</p>
                    <p><strong>3.</strong> Choose whether to generate a React component or static HTML.</p>
                    <div className="ml-4 space-y-1">
                      <p><strong>React Component:</strong> Creates a component for the codebase</p>
                      <p><strong>Static HTML:</strong> Creates standalone HTML for embedding</p>
                    </div>
                    <p><strong>4.</strong> Generate and copy the output.</p>
                    <p><strong>5.</strong> For React: Add to <code>client/src/pages/mentorship/lectures/</code></p>
                    <p><strong>6.</strong> For HTML: Save as <code>.html</code> file and host for embedding</p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
        
        <Footer />
      </div>
    </MentorshipAccessGate>
  );
};

export default LectureGenerator;